import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Cast, Eye } from 'lucide-react';
import { useT } from '../i18n';
import {
  useCollab, startPresenting, stopPresenting, stopFollowing, followOffer,
  publishView, presenterValue, onPresenterView, initials,
} from './live';

// "Mode collaborateur" (live.ts): the person who turns it on presents; the
// others on the same project follow their screen. Followed here, for every
// page: the page itself (the address, without its tab — each page follows its
// own tab state) and the scroll of the main area. What is open or folded in a
// page follows through useFollowedState. Plus the bar saying who presents /
// who follows, with its stop / follow-again buttons.

const hueStyle = (userId: number) => ({ ['--live-hue' as string]: String((userId * 67) % 360) });

/** The address without its `tab` parameter (the page's own followed state sets the tab). */
function baseHash(hash: string): string {
  const [page, query = ''] = hash.replace(/^#/, '').split('?');
  const params = new URLSearchParams(query);
  params.delete('tab');
  const rest = params.toString();
  return `#${page || 'home'}${rest ? `?${rest}` : ''}`;
}

const scroller = () => document.querySelector<HTMLElement>('main.content');

// Where the main area is scrolled, as the element at a third of its height (an
// element with an id or a data-live-anchor) and the point inside it — so it
// lands on the same content whatever each one's screen size — plus a fraction
// of the whole height as a fallback.
interface ScrollSpot { a: string | null; fy: number; f: number }
const lookLine = (box: DOMRect) => Math.min(120, box.height / 3);

function readScroll(el: HTMLElement): ScrollSpot {
  const box = el.getBoundingClientRect();
  const y = box.top + lookLine(box);
  const max = el.scrollHeight - el.clientHeight;
  const spot: ScrollSpot = { a: null, fy: 0, f: max > 0 ? el.scrollTop / max : 0 };
  for (const hit of document.elementsFromPoint(box.left + box.width / 2, y)) {
    if (!el.contains(hit) || hit === el) continue;
    const anchor = hit.closest<HTMLElement>('[data-live-anchor], [id]');
    if (!anchor || anchor === el || !el.contains(anchor)) continue;
    const r = anchor.getBoundingClientRect();
    spot.a = anchor.dataset.liveAnchor ? `a:${anchor.dataset.liveAnchor}` : `i:${anchor.id}`;
    spot.fy = r.height ? (y - r.top) / r.height : 0;
    break;
  }
  return spot;
}

function applyScroll(el: HTMLElement, spot: ScrollSpot): void {
  const box = el.getBoundingClientRect();
  let target: HTMLElement | null = null;
  if (spot.a?.startsWith('a:')) target = el.querySelector(`[data-live-anchor="${CSS.escape(spot.a.slice(2))}"]`);
  else if (spot.a?.startsWith('i:')) target = el.querySelector(`#${CSS.escape(spot.a.slice(2))}`);
  const top = target
    ? el.scrollTop + target.getBoundingClientRect().top - box.top + spot.fy * target.getBoundingClientRect().height - lookLine(box)
    : spot.f * (el.scrollHeight - el.clientHeight);
  if (Math.abs(el.scrollTop - top) > 4) el.scrollTo({ top, behavior: 'smooth' });
}

/** Mounted once in the shell: follows / publishes the page and the scroll, and shows the bar. */
export function CollabLayer() {
  const { t } = useT();
  const collab = useCollab();
  const { presenting, following } = collab;

  // Presenting: my page and my scroll go out.
  useEffect(() => {
    if (!presenting) return;
    const sendHash = () => publishView('nav:hash', baseHash(window.location.hash));
    let timer: ReturnType<typeof setTimeout> | undefined;
    const sendScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => { const el = scroller(); if (el) publishView('nav:scroll', readScroll(el)); }, 150);
    };
    const sendAll = () => { sendHash(); sendScroll(); };
    sendAll();
    const el = scroller();
    window.addEventListener('hashchange', sendAll);
    window.addEventListener('pmo:collab-publish', sendAll);
    el?.addEventListener('scroll', sendScroll, { passive: true });
    window.addEventListener('resize', sendScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('hashchange', sendAll);
      window.removeEventListener('pmo:collab-publish', sendAll);
      el?.removeEventListener('scroll', sendScroll);
      window.removeEventListener('resize', sendScroll);
    };
  }, [presenting]);

  // Following: go where the presenter is, scroll where they are.
  useEffect(() => {
    if (!following) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const scrollNow = () => {
      const spot = presenterValue('nav:scroll') as ScrollSpot | undefined;
      const el = scroller();
      if (spot && el) applyScroll(el, spot);
    };
    const goNow = () => {
      const hash = presenterValue('nav:hash') as string | undefined;
      if (!hash || baseHash(window.location.hash) === hash) return false;
      window.location.hash = hash.slice(1);
      return true;
    };
    const onView = (key: string | null) => {
      if (key === null || key === 'nav:hash') {
        if (goNow() || key === null) {
          // The page builds itself after loading: scroll again once it is there.
          timers.push(setTimeout(scrollNow, 400), setTimeout(scrollNow, 1200));
        }
      }
      if (key === null || key === 'nav:scroll') scrollNow();
    };
    onView(null);
    const off = onPresenterView(onView);
    return () => { off(); timers.forEach(clearTimeout); };
  }, [following?.conn]);

  let bar: React.ReactNode = null;
  if (presenting) {
    const names = collab.followers.map(p => p.name).join(', ');
    bar = (
      <div className="collab-bar is-presenting" role="status">
        <Cast size={14} />
        <span className="collab-bar-text">
          <strong>{t('collab.presenting')}</strong>
          {' · '}
          {collab.followers.length
            ? t(collab.followers.length > 1 ? 'collab.followersMany' : 'collab.followersOne', { n: collab.followers.length, names })
            : t('collab.followersNone')}
        </span>
        <button type="button" className="btn btn-sm collab-bar-btn" onClick={stopPresenting}>{t('collab.stop')}</button>
      </div>
    );
  } else if (following) {
    bar = (
      <div className="collab-bar" role="status" style={hueStyle(following.userId)}>
        <span className="live-avatar live-avatar--sm">{initials(following.name)}</span>
        <span className="collab-bar-text">{t('collab.following', { name: following.name })}</span>
        <button type="button" className="btn btn-sm collab-bar-btn" onClick={stopFollowing}>{t('collab.unfollow')}</button>
      </div>
    );
  } else if (collab.offer) {
    bar = (
      <div className="collab-bar is-offer" role="status" style={hueStyle(collab.offer.userId)}>
        <span className="live-avatar live-avatar--sm">{initials(collab.offer.name)}</span>
        <span className="collab-bar-text">{t('collab.offer', { name: collab.offer.name })}</span>
        <button type="button" className="btn btn-sm collab-bar-btn" onClick={followOffer}><Eye size={12} /> {t('collab.follow')}</button>
      </div>
    );
  }
  return bar ? createPortal(bar, document.body) : null;
}

/** "Mode collaborateur" button (project page header): start / stop presenting. */
export function CollabButton() {
  const { t } = useT();
  const { presenting } = useCollab();
  return (
    <button
      type="button"
      className={`btn btn-sm ${presenting ? 'btn-accent' : 'btn-ghost'} collab-btn`}
      aria-pressed={presenting}
      title={t(presenting ? 'collab.stopHelp' : 'collab.startHelp')}
      onClick={() => (presenting ? stopPresenting() : startPresenting())}
    >
      <Cast size={14} /> {t('collab.button')}
    </button>
  );
}
